import Controller from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import WarehouseSyncModel from 'cio/models/warehouse-sync';
import { WarehouseDestination, getWarehouseDestinationName } from 'cio/utils/warehouse';
import AnalyticsService from 'cio/services/analytics';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import AccountService from 'cio/services/account';

export default class WarehouseTypeIndexController extends Controller {
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service account!: AccountService;

  @tracked isShowingSchemaUpdateModal = false;
  @tracked isShowingPeopleSyncDisabledAlert = false;
  @tracked isShowingOutputsSyncDisableAlert = false;
  @tracked isShowingSubjectsSyncDisableAlert = false;

  redshiftImage = new URL('cio/assets/images/integrations/redshift.svg', import.meta.url).toString();
  bigqueryImage = new URL('cio/assets/images/integrations/bigquery.svg', import.meta.url).toString();
  snowflakeImage = new URL('cio/assets/images/integrations/snowflake.svg', import.meta.url).toString();

  declare model: { sync: WarehouseSyncModel; type: WarehouseDestination };

  setDefaults() {
    this.isShowingSchemaUpdateModal = false;
  }

  get hasActiveSyncToSameWarehouse() {
    return this.model.sync?.type === this.model.type;
  }

  get hasActiveSyncToDifferentWarehouse() {
    return this.model.sync && this.model.sync.type !== this.model.type;
  }

  get showPremiumLabel() {
    return this.account.planFeatures.warehouseSync.showPremiumLabel;
  }

  get destinationName() {
    return getWarehouseDestinationName(this.model.type);
  }

  @action
  toggleSchemaVersionUpdateModal() {
    this.isShowingSchemaUpdateModal = !this.isShowingSchemaUpdateModal;
  }

  disableSyncTask = dropTask(async () => {
    let { sync } = this.model;

    try {
      await sync.destroyRecord();
      this.send('reloadModel');
      this.analytics.track('warehouse', 'disconnect_sync_completed', { model: this.model.sync });
    } catch {
      sync.rollbackAttributes();
    }
  });

  updateSchemaVersionTask = dropTask(async (settings: Partial<Record<keyof WarehouseSyncModel, boolean>>) => {
    let { sync } = this.model;
    let { outputs: oldOutputsValue, people: oldPeopleValue, subjects: oldSubjectsValue, version: oldVersion } = sync;

    try {
      Object.entries(settings).forEach(([key, value]: [keyof WarehouseSyncModel, boolean]) => sync.set(key, value));
      sync.set('updateType', 'schema_version');
      sync.set('version', 4);
      await sync.save();
      this.isShowingSchemaUpdateModal = false;
      this.isShowingPeopleSyncDisabledAlert = oldVersion < 3 && oldPeopleValue;
      this.isShowingOutputsSyncDisableAlert = oldOutputsValue;
      this.isShowingSubjectsSyncDisableAlert = oldSubjectsValue;
      this.alerts.success('warehouse-sync.schema-update', 'Schema version for your sync updated');
    } catch (err) {
      sync.rollbackAttributes();
      this.apiErrors.report(err);
    }
  });
}

declare module '@ember/controller' {
  interface Registry {
    'integrations.warehouse.type.index': WarehouseTypeIndexController;
  }
}
